// React
import React, { Component } from 'react'
import PropTypes from 'prop-types'

// Libraries

// Components
import BQuantityInput from 'ui/blocks/QuantityInput'
import InputGroup from 'ui/blocks/InputGroup'
import Icon from 'ui/components/Icon'
import Tooltip from 'ui/components/Tooltip'

// Shared

/**
 * Input for quantities or numbers
 *
 * @example
 *   <QuantityInput field={field} min="0" max={itemsAvailable}  />
 */
export default class QuantityInput extends Component {
  static displayName = 'QuantityInput'

  static propTypes = {
    min: PropTypes.number,
    max: PropTypes.number,
    field: PropTypes.object,
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
    disabled: PropTypes.bool,
    disabledTooltip: PropTypes.any,
    autoFocus: PropTypes.bool,
    allowEmpty: PropTypes.bool
  }

  static defaultProps = {
    size: 'md',
    field: {}
  }

  handleAdd = () => {
    const { value, onButtonClick } = this.props.field
    const { max } = this.props

    let tempValue = value

    if (typeof value === 'string') {
      tempValue = parseInt(value)
    }

    if (max == null || tempValue + 1 <= max) {
      // Use uniary + to coerce value to number
      const newValue = +tempValue + 1

      if (onButtonClick) {
        onButtonClick(newValue)
      } else {
        this.setFieldValue(newValue)
      }
    }
  }

  handleSubtract = () => {
    const { value, onButtonClick } = this.props.field
    const { min } = this.props

    let tempValue = value

    if (typeof value === 'string') {
      tempValue = parseInt(value)
    }

    if (min == null || tempValue - 1 >= min) {
      // Use uniary + to coerce value to number
      const newValue = +tempValue - 1

      if (onButtonClick) {
        onButtonClick(newValue)
      } else {
        this.setFieldValue(newValue)
      }
    }
  }

  handleChange = (e) => {
    const { onChange, onButtonClick } = this.props.field

    // Only use custom onChange when we have our special handlers (onButtonClick indicates custom usage)
    if (onChange && onButtonClick) {
      onChange(e?.target?.value)
    } else {
      this.setFieldValue(e?.target?.value)
    }
  }

  handleBlur = (e) => {
    const { onBlur } = this.props.field

    onBlur && onBlur(e)
  }

  setFieldValue = (rawValue) => {
    const { onChange } = this.props.field
    const { min, max, allowEmpty } = this.props

    if (rawValue === '' && allowEmpty) {
      onChange('')
      return
    }

    // Special handling for negative number input - allow the minus sign
    if (rawValue === '-' && min !== undefined && min < 0) {
      onChange(rawValue)
      return
    }

    let value = Math.floor(Number(rawValue))

    if (min !== undefined && min > value) {
      value = min
    } else if (max !== undefined && max < value) {
      value = max
    }

    if (!Number.isNaN(value)) {
      onChange(value)
    }
  }

  render () {
    const { field, disabled, size, max, min, autoFocus, disabledTooltip, ...otherProps } = this.props
    const maxReached = field.value >= max
    const minReached = field.value <= min

    // Extract custom handlers from field object to avoid passing them as HTML attributes
    const { onChange: _onChange, onBlur: _onBlur, onKeyPress, onButtonClick: _onButtonClick, ...fieldProps } = field

    return (
      <BQuantityInput disabled={disabled} size={size}>
        <InputGroup preserveBorders>
          <BQuantityInput.Input
            max={max}
            min={min}
            data-tid="QuantityInput"
            {...fieldProps}
            {...otherProps}
            disabled={disabled}
            size={size}
            autoFocus={autoFocus}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            onKeyPress={onKeyPress}
          />
          <BQuantityInput.ControlContainer>
            <BQuantityInput.Control
              plus
              disabled={maxReached || disabled}
              onClick={!maxReached && !disabled ? this.handleAdd : undefined}
              size={size}
              data-tid="Bump quantity up"
            >
              <Tooltip
                direction="top"
                content={(maxReached || disabled) && (disabledTooltip?.max || (typeof disabledTooltip === 'string' ? disabledTooltip : null))}
              >
                <Icon icon="plus" />
              </Tooltip>
            </BQuantityInput.Control>
            <BQuantityInput.Control
              minus
              disabled={minReached || disabled}
              onClick={!minReached && !disabled ? this.handleSubtract : undefined}
              size={size}
              data-tid="Bump quantity down"
            >
              <Tooltip
                direction="bottom"
                content={(minReached || disabled) && (disabledTooltip?.min || (typeof disabledTooltip === 'string' ? disabledTooltip : null))}
              >
                <Icon icon="minus" />
              </Tooltip>
            </BQuantityInput.Control>
          </BQuantityInput.ControlContainer>
        </InputGroup>
      </BQuantityInput>
    )
  }
}
